import { rasterAssetDataUrl } from "@/document/assets/rasterAssets";
import { oklchToCss, parseColor, type OklchColor } from "@/document/model/color";
import { formatHexRgbLabel } from "@/document/model/colorString";
import { normalizeGradientPaint, sampleGradientPaint } from "@/document/model/styles";
import type { Paint, StrokeAlign } from "@/document/model/styles";
import { GripVertical } from "lucide-react";
import { useRef, useState, type DragEvent, type ReactNode } from "react";
import { Metric } from "./Metric";
import { PaintColorInput } from "./PaintColorInput";
import { PaintRowContextMenu } from "./PaintRowContextMenu";
import { PanelSection, PanelSectionAddButton, PanelSectionHeaderActions, PanelSectionStyleButton } from "./PanelSection";
import { imagePaintNameLabel, opacityForPaint } from "./paintStyles";

export type PaintDropPlacement = "before" | "after";

export interface PaintReorderProps {
  index: number;
  mimeType: string;
  dropPlacement?: PaintDropPlacement;
  dragging?: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverPlacement: (placement: PaintDropPlacement | undefined) => void;
  onReorder: (fromIndex: number, hoverIndex: number, placement: PaintDropPlacement) => void;
}

/**
 * Renders fill rows in stack order (top-most first) and owns the shared drop
 * indicator so only one insertion line is visible while dragging.
 */
export function FillPaintStack({
  fills,
  mimeType,
  reorderable = true,
  onReorder,
  children,
}: {
  fills: Paint[];
  mimeType: string;
  reorderable?: boolean;
  onReorder: (fromIndex: number, hoverIndex: number, placement: PaintDropPlacement) => void;
  children: (args: {
    index: number;
    paint: Paint;
    reorder?: PaintReorderProps;
  }) => ReactNode;
}) {
  const [dropIndicator, setDropIndicator] = useState<{ index: number; placement: PaintDropPlacement } | undefined>();
  const [draggingIndex, setDraggingIndex] = useState<number | undefined>();
  const draggingIndexRef = useRef<number | undefined>(undefined);

  return (
    <>
      {fills.map((_, index) => index).reverse().map(index => {
        const paint = fills[index];
        if (!paint) {
          return null;
        }
        return children({
          index,
          paint,
          reorder: reorderable ? {
            index,
            mimeType,
            dropPlacement: dropIndicator?.index === index ? dropIndicator.placement : undefined,
            dragging: draggingIndex === index,
            onDragStart: () => {
              draggingIndexRef.current = index;
              setDraggingIndex(index);
            },
            onDragEnd: () => {
              draggingIndexRef.current = undefined;
              setDraggingIndex(undefined);
              setDropIndicator(undefined);
            },
            onDragOverPlacement: placement => {
              if (draggingIndexRef.current === index || placement === undefined) {
                setDropIndicator(current => current?.index === index ? undefined : current);
                return;
              }
              setDropIndicator({ index, placement });
            },
            onReorder: (fromIndex, hoverIndex, placement) => {
              draggingIndexRef.current = undefined;
              setDraggingIndex(undefined);
              setDropIndicator(undefined);
              onReorder(fromIndex, hoverIndex, placement);
            },
          } : undefined,
        });
      })}
    </>
  );
}

export function PaintSection({
  title,
  disabled,
  paint,
  mixed = false,
  width,
  align,
  onColorClick,
  onColorCommit,
  onAdd,
  onRemove,
  onDuplicate,
  onOpacityCommit,
  onWidthCommit,
  onAlignCommit,
  compactTitle = false,
  hideCompactTitle = false,
  reorder,
  onOpenStyles,
  appliedStyle,
}: {
  title: string;
  disabled: boolean;
  paint?: Paint;
  mixed?: boolean;
  width?: number;
  align?: StrokeAlign;
  compactTitle?: boolean;
  hideCompactTitle?: boolean;
  reorder?: PaintReorderProps;
  onColorClick: () => void;
  onColorCommit?: (color: OklchColor) => void;
  onAdd?: () => void;
  onRemove?: () => void;
  onDuplicate?: () => void;
  onOpacityCommit?: (opacity: number) => void;
  onWidthCommit?: (width: number) => void;
  onAlignCommit?: (align: StrokeAlign) => void;
  onOpenStyles?: () => void;
  appliedStyle?: ReactNode;
}) {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | undefined>();

  const openContextMenu = (event: React.MouseEvent) => {
    if (disabled || !paint || !onDuplicate) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ x: event.clientX, y: event.clientY });
  };

  const contextMenuNode = contextMenu ? (
    <PaintRowContextMenu
      x={contextMenu.x}
      y={contextMenu.y}
      onDuplicate={onDuplicate}
      onDelete={onRemove}
      onClose={() => setContextMenu(undefined)}
    />
  ) : null;

  if (appliedStyle && !compactTitle) {
    return (
      <PanelSection
        title={title}
        headerRight={onOpenStyles ? (
          <PanelSectionHeaderActions
            styleLabel={`Open ${title.toLowerCase()} styles`}
            onOpenStyles={onOpenStyles}
            addLabel={`Add ${title.toLowerCase()}`}
            onAdd={onAdd ?? (() => undefined)}
          />
        ) : undefined}
      >
        {appliedStyle}
      </PanelSection>
    );
  }

  const color = paint?.type === "solid" ? parseColor(paint.color) : undefined;
  const label = paint?.type === "image" ? "Image" : paint?.type === "linearGradient" || paint?.type === "radialGradient" ? "Gradient" : "Solid";
  const opacity = opacityForPaint(paint);
  const valueLabel = mixed
    ? "Mixed"
    : color
      ? formatHexRgbLabel(color)
      : paint?.type === "image"
        ? imagePaintNameLabel(paint)
        : paint?.type === "linearGradient" || paint?.type === "radialGradient"
          ? gradientLabel(paint)
          : "None";

  const headerRight = (
    <div className="flex items-center gap-1">
      {onOpenStyles ? (
        <PanelSectionStyleButton
          label={`Open ${title.toLowerCase()} styles`}
          onClick={onOpenStyles}
        />
      ) : null}
      {paint ? (
        <button
          type="button"
          disabled={disabled}
          className="rounded px-1.5 py-0.5 text-[11px] text-slate-400 hover:bg-white/10 disabled:opacity-40"
          onClick={onColorClick}
        >
          {label}
        </button>
      ) : null}
      {paint ? (
        <button
          type="button"
          disabled={disabled || !onRemove}
          className="rounded px-1.5 py-0.5 text-[13px] leading-none text-slate-400 hover:bg-white/10 disabled:opacity-40"
          aria-label={`Remove ${title.toLowerCase()}`}
          onClick={onRemove}
        >
          -
        </button>
      ) : (
        <PanelSectionAddButton
          label={`Add ${title.toLowerCase()}`}
          disabled={disabled || !onAdd}
          onClick={onAdd ?? (() => undefined)}
        />
      )}
    </div>
  );

  const body = paint || mixed ? (
    <>
      <div className="flex h-7 min-w-0 items-center gap-1.5 rounded bg-white/[0.04] px-1.5">
        <button
          type="button"
          disabled={disabled}
          aria-label={`${title} color`}
          onClick={onColorClick}
          className="size-5 shrink-0 rounded-sm border border-white/20 disabled:opacity-40"
          style={{ background: mixed ? mixedFillBackground() : paintToCssBackground(paint) }}
        />
        {color && onColorCommit ? (
          <PaintColorInput
            color={color}
            disabled={disabled}
            onCommit={onColorCommit}
          />
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={onColorClick}
            className="flex min-w-0 flex-1 overflow-hidden text-left disabled:opacity-40"
          >
            <span className="min-w-0 truncate font-mono text-[11px] text-slate-200" title={valueLabel}>
              {valueLabel}
            </span>
          </button>
        )}
        {onOpacityCommit ? (
          <div className="shrink-0">
            <Metric
              label="%"
              value={opacity}
              disabled={disabled || !paint || mixed}
              compact
              min={0}
              max={100}
              onCommit={onOpacityCommit}
            />
          </div>
        ) : null}
        {onWidthCommit ? (
          <div className="shrink-0">
            <Metric
              label="W"
              value={width}
              disabled={disabled || !paint}
              compact
              min={0}
              onCommit={onWidthCommit}
            />
          </div>
        ) : null}
      </div>
      {onAlignCommit ? (
        <div className="mt-2 grid grid-cols-3 gap-1 rounded bg-white/[0.04] p-1">
          {(["inside", "center", "outside"] as StrokeAlign[]).map(option => (
            <button
              key={option}
              type="button"
              disabled={disabled || !paint}
              onClick={() => onAlignCommit(option)}
              className={`h-7 rounded px-2 text-[11px] capitalize transition-colors disabled:opacity-40 ${
                (align ?? "inside") === option
                  ? "bg-sky-500/20 text-sky-100"
                  : "text-slate-400 hover:bg-white/10"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
    </>
  ) : null;

  const reorderHandle = reorder ? (
    <button
      type="button"
      draggable
      aria-label={`Reorder ${title.toLowerCase()}`}
      title="Drag to reorder"
      className="grid size-5 shrink-0 cursor-grab place-items-center rounded text-slate-600 hover:bg-white/[0.06] hover:text-slate-400 active:cursor-grabbing"
      onDragStart={event => {
        event.dataTransfer.setData(reorder.mimeType, String(reorder.index));
        event.dataTransfer.effectAllowed = "move";
        reorder.onDragStart();
      }}
      onDragEnd={reorder.onDragEnd}
    >
      <GripVertical className="size-3.5" aria-hidden="true" />
    </button>
  ) : null;

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!reorder || !event.dataTransfer.types.includes(reorder.mimeType)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (reorder.dragging) {
      reorder.onDragOverPlacement(undefined);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const placement: PaintDropPlacement = event.clientY < rect.top + rect.height / 2 ? "before" : "after";
    reorder.onDragOverPlacement(placement);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!reorder) {
      return;
    }
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) {
      return;
    }
    reorder.onDragOverPlacement(undefined);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!reorder) {
      return;
    }
    event.preventDefault();
    const fromIndex = Number.parseInt(event.dataTransfer.getData(reorder.mimeType), 10);
    const placement = reorder.dropPlacement
      ?? (event.clientY < event.currentTarget.getBoundingClientRect().top + event.currentTarget.getBoundingClientRect().height / 2
        ? "before"
        : "after");
    if (Number.isInteger(fromIndex) && fromIndex !== reorder.index) {
      reorder.onReorder(fromIndex, reorder.index, placement);
    } else {
      reorder.onDragOverPlacement(undefined);
    }
  };

  if (compactTitle && hideCompactTitle) {
    return (
      <>
        <div
          className={`relative flex min-w-0 items-center gap-1 ${reorder?.dragging ? "opacity-45" : ""}`}
          onContextMenu={openContextMenu}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {reorder?.dropPlacement === "before" ? <DropLine position="top" /> : null}
          {reorder?.dropPlacement === "after" ? <DropLine position="bottom" /> : null}
          {reorderHandle}
          <div className="min-w-0 flex-1">{body}</div>
          {paint ? (
            <button
              type="button"
              disabled={disabled || !onRemove}
              className="grid size-6 shrink-0 place-items-center rounded text-[13px] leading-none text-slate-400 hover:bg-white/10 disabled:opacity-40"
              aria-label={`Remove ${title.toLowerCase()}`}
              title={`Remove ${title.toLowerCase()}`}
              onClick={onRemove}
            >
              -
            </button>
          ) : null}
        </div>
        {contextMenuNode}
      </>
    );
  }

  if (compactTitle) {
    return (
      <>
        <div
          className={`relative min-w-0 ${reorder?.dragging ? "opacity-45" : ""}`}
          onContextMenu={openContextMenu}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {reorder?.dropPlacement === "before" ? <DropLine position="top" /> : null}
          {reorder?.dropPlacement === "after" ? <DropLine position="bottom" /> : null}
          <div className="mb-1 flex min-w-0 items-center justify-between">
            <div className="flex min-w-0 items-center">
              {reorderHandle}
              {!hideCompactTitle ? <div className="text-[11px] uppercase text-slate-500">{title}</div> : null}
            </div>
            {headerRight}
          </div>
          {body}
        </div>
        {contextMenuNode}
      </>
    );
  }

  return (
    <>
      <PanelSection title={title} headerRight={headerRight}>
        <div onContextMenu={openContextMenu}>
          {body}
        </div>
      </PanelSection>
      {contextMenuNode}
    </>
  );
}

function DropLine({ position }: { position: "top" | "bottom" }) {
  return (
    <span
      className={`pointer-events-none absolute left-0 right-0 z-10 h-px bg-sky-300 shadow-[0_0_0_1px_rgba(125,211,252,0.35)] ${
        position === "top" ? "top-0" : "bottom-0"
      }`}
      aria-hidden="true"
    />
  );
}

function mixedFillBackground(): string {
  return "linear-gradient(135deg, #64748b 0%, #64748b 45%, #cbd5e1 45%, #cbd5e1 55%, #64748b 55%, #64748b 100%)";
}

function paintToCssBackground(paint?: Paint): string {
  if (!paint) {
    return "transparent";
  }

  if (paint.type === "solid") {
    return oklchToCss(parseColor(paint.color));
  }

  if (paint.type === "image") {
    const source = rasterAssetDataUrl(paint.assetId, paint.source);
    return source ? `url("${source}") center / cover` : "transparent";
  }

  const normalized = normalizeGradientPaint(paint);
  const stops = sampleGradientPaint(normalized, 16)
    .map(stop => `${oklchToCss(stop.color)} ${Math.round(stop.position * 100)}%`)
    .join(", ");
  return normalized.type === "radialGradient"
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${normalized.angle + 90}deg, ${stops})`;
}

function gradientLabel(paint: Exclude<Paint, { type: "solid" }>): string {
  if (paint.type === "image") {
    return imagePaintNameLabel(paint);
  }

  const normalized = normalizeGradientPaint(paint);
  return normalized.type === "radialGradient"
    ? `Radial ${normalized.colorSpace.toUpperCase()}`
    : `${Math.round(normalized.angle)}deg ${normalized.colorSpace.toUpperCase()}`;
}
